import { z } from 'zod'
import { Product } from '@/types'

export const Domain = z.object({
  id: z.number().optional(),
  name: z.string(),
  name_ace: z.string().optional(),
  status: z
    .enum([
      'used',
      'free',
      'invalid',
      'unsupported',
      'internal',
      'progress',
      'yours',
      'error'
    ])
    .optional(),
  // The registration's own state, sent with a subscription's domain edge.
  // Distinct from `status` above, which is the search result's availability.
  registration_status: z
    .enum([
      'active',
      'pending_delete',
      'deleted',
      'trade_out',
      'external',
      'suspended',
      'transfer_pending'
    ])
    .optional(),
  tld: z.string().optional(),
  promo: z.boolean().optional(),
  premium: z.boolean().optional(),
  transfer_available: z.boolean().optional(),
  transfer_price: z.number().optional(),
  products: z
    .object({
      create: Product.optional(),
      transfer: Product.optional(),
      trade: Product.optional(),
      restore: Product.optional(),
      update: Product.optional(),
      renew: Product.optional()
    })
    .nullish()
})

export type Domain = z.infer<typeof Domain>

/**
 * Request for POST /domain search endpoint
 * When domain has no TLD, searches across all TLDs in catalog
 */
export const DomainSearchRequest = z.object({
  domain: z.string(),
  status: z.enum(['free', 'used']).optional(),
  featured: z.boolean().optional(),
  limit: z.number().max(50).optional(),
  offset: z.number().optional()
})

export type DomainSearchRequest = z.infer<typeof DomainSearchRequest>

/**
 * Response from POST /domain search endpoint
 */
export const DomainSearchResponse = z.object({
  domains: z.array(Domain),
  total_count: z.number(),
  limit: z.number(),
  offset: z.number()
})

export type DomainSearchResponse = z.infer<typeof DomainSearchResponse>

/**
 * Cost of a registry redemption. `gross_cents` is what is charged; net and tax
 * are the breakdown of that same figure, not additions to it.
 */
export const RestoreQuote = z.object({
  domain: z.string(),
  net_cents: z.number().int(),
  tax_cents: z.number().int(),
  gross_cents: z.number().int(),
  tax_rate: z.number(),
  currency: z.string()
})
export type RestoreQuote = z.infer<typeof RestoreQuote>

/** `chargeable` is false for a free restore, and then `quote` is absent. */
export const RestoreQuoteResponse = z.object({
  chargeable: z.boolean(),
  quote: RestoreQuote.optional()
})
export type RestoreQuoteResponse = z.infer<typeof RestoreQuoteResponse>

/**
 * The own contract of a domain bundled with a hosting package, for one year.
 * `gross_cents` is invoiced at `starts_at` (Unix seconds, the registry
 * expiry); net and tax are its breakdown. `ending` while the domain is
 * pending deletion because its package was cancelled.
 */
export const DomainContractQuote = z.object({
  domain: z.string(),
  net_cents: z.number().int(),
  tax_cents: z.number().int(),
  gross_cents: z.number().int(),
  tax_rate: z.number(),
  currency: z.string(),
  starts_at: z.number().int(),
  package_id: z.number().int(),
  ending: z.boolean()
})
export type DomainContractQuote = z.infer<typeof DomainContractQuote>

export const DomainContractResult = z.object({
  subscription_id: z.number().int(),
  starts_at: z.number().int()
})
export type DomainContractResult = z.infer<typeof DomainContractResult>
